import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { Trophy, Calendar, ArrowRight, Flame, ShieldCheck, Sparkles } from "lucide-react"
import type { Game } from "@/graphql/types"

interface AnimatedMetricValueProps {
  value: number
  loading: boolean
  delay?: number
}

const AnimatedMetricValue = ({ value, loading, delay = 0 }: AnimatedMetricValueProps) => {
  const [displayValue, setDisplayValue] = useState(0)

  useEffect(() => {
    if (loading) {
      setDisplayValue(0)
      return
    }

    let frameId = 0
    let timerId: ReturnType<typeof setTimeout> | undefined
    const duration = 900

    const startAnimation = () => {
      const startTime = performance.now()

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)
        const easedProgress = 1 - Math.pow(1 - progress, 3)
        const nextValue = Math.round(value * easedProgress)

        setDisplayValue(nextValue)

        if (progress < 1) {
          frameId = requestAnimationFrame(animate)
        }
      }

      frameId = requestAnimationFrame(animate)
    }

    if (delay > 0) {
      timerId = setTimeout(startAnimation, delay)
    } else {
      startAnimation()
    }

    return () => {
      if (frameId) cancelAnimationFrame(frameId)
      if (timerId) clearTimeout(timerId)
    }
  }, [delay, loading, value])

  if (loading) return <>--</>
  return <>{displayValue.toLocaleString()}</>
}

function formatCompact(value: number): string {
  if (value >= 1000) {
    const k = value / 1000
    return `${Number.isInteger(k) ? k : k.toFixed(1)}k`
  }
  return value.toLocaleString()
}

interface HeroSectionProps {
  games: Game[]
  loading: boolean
}

export const HeroSection = ({ games, loading }: HeroSectionProps) => {
  const navigate = useNavigate()

  const totalTournaments = games.reduce((acc, game) => acc + Number(game.totalTournaments || 0), 0)
  const activeGames = games.filter((game) => game.isActive).length
  const gamesWithSeason = games.filter((game) => Boolean(game.urlActiveSeason)).length

  const metricItems = [
    { label: "Juegos", value: activeGames, delay: 0 },
    { label: "Torneos", value: totalTournaments, delay: 120 },
    { label: "Temporadas", value: gamesWithSeason, delay: 240 },
  ]

  return (
    <section className="relative overflow-hidden px-3 sm:px-4 pt-5 pb-4 sm:pt-8 sm:pb-6 md:pt-14 md:pb-10">
      <div className="container mx-auto max-w-7xl p-0">
        <div className="grid gap-4 sm:gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div className="space-y-3 sm:space-y-5">
            <Badge className="inline-flex gap-1.5 border-primary/25 bg-primary/10 px-2.5 py-0.5 text-xs sm:px-3 sm:py-1 sm:text-sm text-primary">
              <Flame className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
              <span className="truncate">Escena competitiva MyL {new Date().getFullYear()}</span>
            </Badge>

            <h1 className="text-3xl sm:text-4xl font-black leading-tight tracking-tight md:text-6xl">
              Tu próxima
              <span className="block bg-gradient-to-r from-primary via-cyan-400 to-primary bg-[length:200%_200%] bg-clip-text text-transparent animate-gradient">
                victoria empieza aquí
              </span>
            </h1>

            <p className="max-w-2xl text-sm sm:text-lg leading-relaxed text-muted-foreground md:text-xl">
              Gestiona torneos, sigue temporadas activas y mide tu rendimiento en una plataforma
              pensada para competir y ser parte de Mitos y Leyendas.
            </p>

            <div className="flex flex-col gap-2.5 sm:gap-3 sm:flex-row">
              <Button
                size="lg"
                className="h-11 sm:h-12 bg-gradient-primary px-5 sm:px-7 text-sm sm:text-base shadow-[0_8px_30px_hsl(var(--primary)/0.35)]"
                onClick={() => navigate("/tournaments")}
              >
                <Calendar className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Explorar Eventos
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              <Button
                variant="outline"
                size="lg"
                className="h-11 sm:h-12 px-5 sm:px-7 text-sm sm:text-base"
                onClick={() => navigate("/ranking")}
              >
                <Trophy className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Ver Ranking
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/70 px-2.5 py-1 sm:px-3 sm:py-1.5">
                <ShieldCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-500 shrink-0" />
                Resultados verificados
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/70 px-2.5 py-1 sm:px-3 sm:py-1.5">
                <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-500 shrink-0" />
                Eventos diarios
              </span>
            </div>
          </div>

          <Card className="border-primary/15 bg-card/65 backdrop-blur-xl shadow-[0_10px_45px_rgba(2,6,23,0.08)]">
            <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-muted/40 dark:border-gray-700">
              <Trophy className="h-4 w-4 text-primary shrink-0" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Pulso Competitivo
              </span>
            </div>
            <CardContent className="px-3 sm:px-6 pt-4 pb-4 space-y-3 sm:space-y-4">
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {metricItems.map((metric, index) => (
                  <motion.div
                    key={metric.label}
                    className="rounded-xl border border-border/70 bg-background/70 p-2 sm:p-3 text-center"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: index * 0.08 }}
                  >
                    <p className="text-xl sm:text-2xl font-bold tabular-nums">
                      <span className="sm:hidden">
                        {loading ? "--" : formatCompact(metric.value)}
                      </span>
                      <span className="hidden sm:inline">
                        <AnimatedMetricValue
                          value={metric.value}
                          loading={loading}
                          delay={metric.delay}
                        />
                      </span>
                    </p>
                    <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground leading-tight">
                      {metric.label}
                    </p>
                  </motion.div>
                ))}
              </div>

              <div className="rounded-2xl border border-border/70 bg-gradient-to-r from-primary/10 via-transparent to-cyan-500/10 p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-muted-foreground">
                  ¿Buscas acción inmediata?
                </p>
                <p className="mt-1 text-sm sm:text-base font-semibold">
                  Revisa las temporadas activas y entra al circuito.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1.5 sm:mt-2 h-auto px-2 text-primary text-xs sm:text-sm"
                  onClick={() => navigate("/tournaments")}
                >
                  Ir al calendario
                  <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  )
}
